import { useEffect, useState } from 'react';

/**
 * Start the offline demo's worker, and say whether it came up.
 *
 * The import is dynamic so the engine, the host and the scenario are a chunk the
 * ordinary app never downloads — a visitor joining a real exercise pays nothing
 * for the demo existing.
 */

export type DemoStatus = 'idle' | 'starting' | 'ready' | 'failed';

export interface DemoState {
  readonly status: DemoStatus;
  readonly error: string | null;
  /** One host across tabs (a `SharedWorker`), rather than one game per tab. */
  readonly shared: boolean;
}

export function useDemoBridge(enabled: boolean): DemoState {
  const [state, setState] = useState<DemoState>({
    status: enabled ? 'starting' : 'idle',
    error: null,
    shared: false,
  });

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    void (async () => {
      try {
        const { startDemoBridge } = await import('./bridge.js');
        const bridge = await startDemoBridge();
        if (!cancelled) setState({ status: 'ready', error: null, shared: bridge.shared });
      } catch (error) {
        if (cancelled) return;
        setState({
          status: 'failed',
          error: error instanceof Error ? error.message : 'The demo could not start.',
          shared: false,
        });
      }
    })();

    // Deliberately not stopped on unmount. The worker holds the game: tearing it
    // down because React re-ran an effect would throw away the exercise, and in
    // StrictMode's double-mount it would do so on every page load.
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return state;
}
